// Thin wrapper over the shared api() fetcher with admin-flavored
// error handling. We keep the underlying transport identical to
// /v1/admin/* uses the same cookie/CSRF model as the user side.

import { api } from '../api';
import { caseDetailQuery, caseFilterQuery, compareQuery, runFilterQuery } from './evals/query';
import type {
  EvalApiError,
  EvalCaseFilter,
  EvalCaseList,
  EvalComparison,
  EvalOverview,
  EvalPinOutcome,
  EvalRevealedResult,
  EvalRole,
  EvalRunDetail,
  EvalRunFilter,
  EvalRunList,
  EvalScheduleView,
  EvalUnpinOutcome,
} from './evals/types';
import type { EvalImportOutcome, RunFileUpload } from './evals/upload';

export async function adminApi<T = unknown>(
  path: string,
  opts: Parameters<typeof api>[1] = {}
): Promise<T> {
  return api<T>(path, opts);
}

/** The error body an admin route answered with, when the request failed with one. */
export function adminApiError(err: unknown): Partial<EvalApiError> {
  const body = (err as { body?: unknown }).body;
  return body !== null && typeof body === 'object' ? (body as Partial<EvalApiError>) : {};
}

/** The message an admin route answered with, or a fallback. */
export function adminApiMessage(err: unknown, fallback: string): string {
  const message = adminApiError(err).message;
  return typeof message === 'string' && message.length > 0 ? message : fallback;
}

const runPath = (id: string) => `/v1/admin/evals/runs/${encodeURIComponent(id)}`;

/** The admin Evals API (specs/api_contracts.md §11.5 and §11.5.1). */
export const evalsApi = {
  overview(days = 30, trendDays = 180): Promise<EvalOverview> {
    return adminApi<EvalOverview>(`/v1/admin/evals/overview?days=${days}&trendDays=${trendDays}`);
  },
  runs(filter: EvalRunFilter): Promise<EvalRunList> {
    return adminApi<EvalRunList>(`/v1/admin/evals/runs${runFilterQuery(filter)}`);
  },
  run(id: string): Promise<EvalRunDetail> {
    return adminApi<EvalRunDetail>(runPath(id));
  },
  cases(id: string, filter: EvalCaseFilter): Promise<EvalCaseList> {
    return adminApi<EvalCaseList>(`${runPath(id)}/results${caseFilterQuery(filter)}`);
  },
  /** One case with its transcripts and deployed definition, for the drawer. */
  caseDetail(
    id: string,
    c: { model: string; role: EvalRole; caseId: string }
  ): Promise<EvalCaseList> {
    return adminApi<EvalCaseList>(`${runPath(id)}/results${caseDetailQuery(c)}`);
  },
  compare(a: string, b?: string | null): Promise<EvalComparison> {
    return adminApi<EvalComparison>(`/v1/admin/evals/compare${compareQuery(a, b)}`);
  },
  pin(id: string, body: { model: string; role: EvalRole; note?: string }): Promise<EvalPinOutcome> {
    return adminApi<EvalPinOutcome>(`${runPath(id)}/pin`, { method: 'POST', body });
  },
  unpin(id: string, body: { model: string; role: EvalRole }): Promise<EvalUnpinOutcome> {
    return adminApi<EvalUnpinOutcome>(`${runPath(id)}/unpin`, { method: 'POST', body });
  },
  /** A test-split result in full. The server audits it before answering. */
  reveal(resultId: string, reason?: string): Promise<EvalRevealedResult> {
    return adminApi<EvalRevealedResult>(
      `/v1/admin/evals/results/${encodeURIComponent(resultId)}/reveal`,
      { method: 'POST', body: reason === undefined ? {} : { reason } }
    );
  },
  /** The weekly scheduled eval (TOM-142): its switch, slot, last fire and alert. */
  schedule(): Promise<EvalScheduleView> {
    return adminApi<EvalScheduleView>('/v1/admin/evals/schedule');
  },
  /** Turn the weekly scheduled eval on or off. The server audits the change. */
  updateSchedule(body: {
    enabled?: boolean;
    weekday?: number;
    hourUtc?: number;
  }): Promise<EvalScheduleView> {
    return adminApi<EvalScheduleView>('/v1/admin/evals/schedule', { method: 'PATCH', body });
  },
  /** Store a run file `--out` wrote (task eval_run_store), gzipped when it can be (TOM-305). */
  importRunFile(upload: RunFileUpload): Promise<EvalImportOutcome> {
    return adminApi<EvalImportOutcome>(
      '/v1/admin/evals/runs/import',
      upload.encoding === 'gzip'
        ? { method: 'POST', raw: { contentType: 'application/gzip', data: upload.data } }
        : { method: 'POST', body: upload.body }
    );
  },
};
